// components/layout/PageShell.tsx — App layout shell with glass bottom navigation

import { useCallback, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Outlet, NavLink, useNavigate, useMatch, useParams, useLocation, matchPath } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNotificationStore, useTreeStore, useAppStore, usePullToRefreshStore } from '@/store';
import { Toast } from '@/components/ui';
import { treesApi } from '@/services/api';
import type { Tree } from '@/types';
import { OfflineBanner } from './OfflineBanner';
import { PullToRefresh } from './PullToRefresh';
import { BookFlipOverlay } from '@/features/home/components/BookFlipOverlay';
import { PageTurnOverlay } from '@/features/tree/components/PageTurnOverlay';
import styles from './PageShell.module.css';

// Routes with their own pan/scroll/gesture handling, an active input, or
// in-progress form state — pull-to-refresh is disabled on these so it can't
// fight their gestures or discard unsaved input by refetching underneath them.
const PULL_TO_REFRESH_DISABLED_ROUTES = [
  '/trees/:treeId/canvas',                   // canvas: pan/pinch-zoom
  '/trees/:treeId/overview',                 // fixed bird's-eye visualization
  '/trees/:treeId/add',                      // form
  '/trees/:treeId/edit-node',                // form
  '/profile/edit',                           // form
  '/profile/privacy',                        // settings toggles
  '/chat/:conversationId',                   // own message-list scroll region
  '/merge/:requestId/resolve',               // in-progress conflict resolution
  '/inner-circle/matrimony/new',             // form
  '/inner-circle/matrimony/edit/:id',        // form
  '/inner-circle/matrimony/draft-for-relative', // form
  '/trees/:treeId/circle/matrimony/new',                // form (book-scoped)
  '/trees/:treeId/circle/matrimony/edit/:id',           // form (book-scoped)
  '/trees/:treeId/circle/matrimony/draft-for-relative', // form (book-scoped)
  '/ai',                                     // own chat scroll region + active input
  '/settings',                               // pure navigation, nothing to refresh
];


// ── SVG Icons (inline — no icon library dependency) ──
function HomeIcon({ active }: { active: boolean }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 11L12 4L20 11V19C20 19.5304 19.7893 20.0391 19.4142 20.4142C19.0391 20.7893 18.5304 21 18 21H6C5.46957 21 4.96086 20.7893 4.58579 20.4142C4.21071 20.0391 4 19.5304 4 19V11Z"
        fill={active ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <path d="M9 21V13H15V21" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
    </svg>
  );
}

function InnerCircleIcon({ active }: { active: boolean }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="3" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.75" />
      <circle cx="16" cy="9" r="2.5" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M3 19C3 15.5 5.5 13 8.5 13C11 13 13.1 14.6 13.8 16.8"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
      <path
        d="M13 15C13.6 13.9 14.9 13.1 16.3 13.1C18.5 13.1 20.3 15 20.3 17.5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ChatIcon({ active }: { active: boolean }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z"
        fill={active ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="5" cy="12" r="1.5" fill="currentColor" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      <circle cx="19" cy="12" r="1.5" fill="currentColor" />
    </svg>
  );
}

function ToolsIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M14.7 6.3a4 4 0 0 1-5.4 5.4L4 17v3h3l5.3-5.3a4 4 0 0 1 5.4-5.4L14.7 12l3-3-1-1.7Z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 6L18 18M18 6L6 18" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function RelationFinderIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="6.5" cy="6.5" r="3" stroke="currentColor" strokeWidth="1.75" />
      <circle cx="17.5" cy="17.5" r="3" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M9 9L15 15"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeDasharray="1.5 3"
      />
    </svg>
  );
}

function ConnectIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M10 14a5 5 0 0 0 7.07 0l2.83-2.83a5 5 0 0 0-7.07-7.07L11.5 5.42"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 10a5 5 0 0 0-7.07 0L4.1 12.83a5 5 0 0 0 7.07 7.07L12.5 18.58"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function UnlinkIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M10 14a5 5 0 0 0 7.07 0l2.83-2.83a5 5 0 0 0-7.07-7.07L11.5 5.42"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 10a5 5 0 0 0-7.07 0L4.1 12.83a5 5 0 0 0 7.07 7.07L12.5 18.58"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M4 4L20 20" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function InviteIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.75" />
      <path d="M3 7L12 13L21 7" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="18" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.75" />
      <circle cx="6" cy="12" r="2.5" stroke="currentColor" strokeWidth="1.75" />
      <circle cx="18" cy="19" r="2.5" stroke="currentColor" strokeWidth="1.75" />
      <path d="M8.2 10.7L15.8 6.3M8.2 13.3L15.8 17.7" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function RemoveIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m2 0-.7 12.1a2 2 0 0 1-2 1.9H9.7a2 2 0 0 1-2-1.9L7 7"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function MergeRequestsIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="6" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.75" />
      <circle cx="6" cy="18" r="2.5" stroke="currentColor" strokeWidth="1.75" />
      <circle cx="18" cy="12" r="2.5" stroke="currentColor" strokeWidth="1.75" />
      <path d="M6 8.5V18M6 8.5C6 12 9 12 13 12H15.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      <path d="M13.5 9.5L16 12L13.5 14.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

interface NavItemDef {
  to: string;
  labelKey: string;
  icon: (active: boolean) => ReactNode;
  badgeCount?: number;
}

interface ToolItemDef {
  key: string;
  labelKey: string;
  icon: ReactNode;
  onSelect: () => void;
  comingSoon?: boolean;
}

export function PageShell() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { treeId: paramTreeId } = useParams();
  // Tools (Link/Unlink/Remove/Check Relation/…) only make sense on the tree
  // canvas itself — not the book index that now lives at the bare /trees/:treeId.
  const treeMatch = useMatch('/trees/:treeId/canvas');
  const isTreeRoute = !!treeMatch;
  const isPullToRefreshDisabled = PULL_TO_REFRESH_DISABLED_ROUTES.some(
    (pattern) => matchPath(pattern, pathname) !== null
  );
  const queryClient = useQueryClient();
  const registeredRefreshHandler = usePullToRefreshStore((s) => s.handler);
  const handleRefresh = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries(),
      registeredRefreshHandler?.(),
    ]);
  }, [queryClient, registeredRefreshHandler]);
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const beginConnect = useTreeStore((s) => s.beginConnect);
  const beginUnlink = useTreeStore((s) => s.beginUnlink);
  const beginRemove = useTreeStore((s) => s.beginRemove);
  const beginCheck = useTreeStore((s) => s.beginCheck);
  const activeTreeId = useAppStore((s) => s.activeTreeId);
  const currentUserId = useAppStore((s) => s.userId);
  // Best-known tree to link/act on: the URL's :treeId while on a tree route,
  // else the last-visited tree, else the backend's "my own tree" alias.
  const currentTreeId = paramTreeId ?? activeTreeId ?? 'me';

  const [isToolsOpen, setIsToolsOpen] = useState(false);
  const [comingSoon, setComingSoon] = useState<{ title: string; description: string } | null>(null);

  // Reuses the ['tree', treeId] query TreeCanvasScreen already populates —
  // just needed here to gate the destructive Remove Person tool to admins.
  const { data: tree } = useQuery({
    queryKey: ['tree', currentTreeId],
    queryFn: async () => (await treesApi.get(currentTreeId)) as unknown as Tree,
    enabled: isTreeRoute,
  });
  const isAdmin = !!currentUserId && !!tree?.adminUserIds.includes(currentUserId);

  const navItems: NavItemDef[] = [
    { to: '/home',    labelKey: 'nav.home',    icon: (a) => <HomeIcon active={a} /> },
    { to: '/inner-circle', labelKey: 'nav.innerCircle', icon: (a) => <InnerCircleIcon active={a} /> },
    { to: '/chat',    labelKey: 'nav.chat',    icon: (a) => <ChatIcon active={a} />, badgeCount: 0 },
    { to: '/settings',labelKey: 'nav.more',    icon: () => <MoreIcon /> },
  ];

  const toolItems: ToolItemDef[] = [
    {
      key: 'link-people',
      labelKey: 'nav.tools.linkPeople',
      icon: <ConnectIcon />,
      onSelect: () => {
        beginConnect();
        navigate(`/trees/${currentTreeId}/canvas`);
      },
    },
    {
      key: 'unlink-people',
      labelKey: 'nav.tools.unlinkPeople',
      icon: <UnlinkIcon />,
      onSelect: () => {
        beginUnlink();
        navigate(`/trees/${currentTreeId}/canvas`);
      },
    },
    ...(isAdmin
      ? [
          {
            key: 'remove-person',
            labelKey: 'nav.tools.removePerson',
            icon: <RemoveIcon />,
            onSelect: () => {
              beginRemove();
              navigate(`/trees/${currentTreeId}/canvas`);
            },
          },
        ]
      : []),
    {
      key: 'check-relation',
      labelKey: 'nav.tools.checkRelation',
      icon: <RelationFinderIcon />,
      onSelect: () => {
        beginCheck();
        navigate(`/trees/${currentTreeId}/canvas`);
      },
    },
    {
      key: 'invite',
      labelKey: 'nav.tools.invite',
      icon: <InviteIcon />,
      onSelect: () => navigate(`/trees/${currentTreeId}/invites`),
    },
    {
      key: 'share-tree',
      labelKey: 'nav.tools.shareTree',
      icon: <ShareIcon />,
      comingSoon: true,
      // Export/share a read-only view or snapshot of the whole tree. Not implemented yet.
      onSelect: () =>
        setComingSoon({
          title: t('nav.tools.shareTree'),
          description: 'Coming soon — share a read-only view of your whole tree.',
        }),
    },
    {
      key: 'suggested-edits',
      labelKey: 'nav.tools.suggestedEdits',
      icon: <MergeRequestsIcon />,
      comingSoon: true,
      // Review additions/edits suggested by claimed members of this tree
      // (new nodes, info updates) before they merge into the tree. Not implemented yet.
      onSelect: () =>
        setComingSoon({
          title: t('nav.tools.suggestedEdits'),
          description: 'Coming soon — review edits and additions suggested by family members.',
        }),
    },
  ];

  const closeTools = () => setIsToolsOpen(false);

  return (
    <div className={styles.shell}>
      <OfflineBanner />
      {/* ── Main content area ── */}
      <PullToRefresh
        className={styles.main}
        id="main-content"
        onRefresh={handleRefresh}
        disabled={isPullToRefreshDisabled}
      >
        <Outlet />
      </PullToRefresh>

      {/* ── Glass Bottom Navigation ── */}
      <nav
        className={isTreeRoute ? styles.bottomNav : `${styles.bottomNav} ${styles.bottomNavCentered}`}
        aria-label="Main navigation"
      >
        <ul className={styles.navPill} role="list">
          {navItems.map((item) => (
            <li key={item.to} className={styles.navItem}>
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  isActive ? `${styles.navLink} ${styles.active}` : styles.navLink
                }
                aria-label={t(item.labelKey)}
              >
                {({ isActive }) => (
                  <span className={styles.navIcon}>
                    {item.icon(isActive)}
                    {item.to === '/settings' && unreadCount > 0 && (
                      <span className={styles.badge} aria-label={`${unreadCount} unread`}>
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>

        {/* ── Tools FAB — tree page only; expands into the quick-action grid ── */}
        {isTreeRoute && (
          <div className={styles.toolsWrap}>
            {isToolsOpen && (
              <div className={styles.toolsMenu} role="menu">
                {toolItems.map((item, i) => (
                  <button
                    key={item.key}
                    role="menuitem"
                    className={styles.toolsMenuItem}
                    style={{ '--tool-i': i } as CSSProperties}
                    onClick={() => {
                      closeTools();
                      item.onSelect();
                    }}
                  >
                    <span className={styles.toolsMenuIcon}>
                      {item.icon}
                      {item.comingSoon && <span className={styles.toolsMenuSoonBadge}>Soon</span>}
                    </span>
                    <span className={styles.toolsMenuLabel}>{t(item.labelKey)}</span>
                  </button>
                ))}
              </div>
            )}

            <button
              className={styles.toolsBtn}
              onClick={() => setIsToolsOpen((v) => !v)}
              aria-label={isToolsOpen ? t('nav.tools.close') : t('nav.tools.open')}
              aria-expanded={isToolsOpen}
            >
              {isToolsOpen ? <CloseIcon /> : <ToolsIcon />}
            </button>
          </div>
        )}
      </nav>

      {isTreeRoute && isToolsOpen && <div className={styles.toolsScrim} onClick={closeTools} />}

      <Toast
        open={!!comingSoon}
        onOpenChange={(open) => {
          if (!open) setComingSoon(null);
        }}
        type="info"
        title={comingSoon?.title ?? ''}
        {...(comingSoon?.description ? { description: comingSoon.description } : {})}
      />

      {/* Survives the Home -> book-index route change it ends in — see
          features/home/bookFlipBus.ts and BookFlipOverlay's own header
          comment for why this has to live here rather than in HomeScreen. */}
      <BookFlipOverlay />

      {/* Survives the book-index -> destination route change it ends in —
          same reasoning, see features/tree/pageTurnBus.ts. */}
      <PageTurnOverlay />
    </div>
  );
}
